<script>
  import Async from '../components/Async.svelte';
  import Button from '../components/Button.svelte';
  import Section from '../components/Section.svelte';
  import Panel from '../components/Panel.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import RulesEditor from '../components/RulesEditor.svelte';
  import { getDatasets, shareEveryTerm, datasetRemoval, deleteDataset } from '../lib/api.js';
  import { HELP } from '../lib/help.js';
  import { nav, go } from '../lib/nav.svelte.js';
  import { mayVisit, can } from '../lib/session.svelte.js';
  import { sharingWords } from '../lib/slices.js';
  import { amount, periodRange, exportState, termText } from '../lib/datasets.js';

  // One dataset: what it holds, the terms it records, what is shared with
  // whom, and where the machinery behind it lives (docs/ref-belonging-plan.md,
  // step 3). The tab is in the URL so a link can open on it.
  const TABS = [
    { id: 'about', label: 'About' },
    { id: 'terms', label: 'Terms' },
    { id: 'sharing', label: 'Sharing' },
    { id: 'hood', label: 'Under the hood' },
  ];
  const tab = $derived(TABS.some((t) => t.id === nav.params.tab) ? nav.params.tab : 'about');
  const open = (id) => go('data/dataset', { id: nav.params.id, tab: id === 'about' ? null : id });

  let datasets = $state(getDatasets());
  const find = ({ datasets }) => datasets.find((d) => d.id === nav.params.id) ?? null;

  // What a cube counts, in the words of the dataset's own terms.
  const counts = (d, cube) => cube.dimensions.map((term) => termText(d, term)).join(' × ');
  const values = (q) => q.unit === 'text' ? 'free text'
    : q.scale ? (q.scale.label ?? q.scale.values.join(' · ')) : '—';
  const shared = (q) => q.neverShared ? 'never' : q.cubes.length ? 'yes' : 'not counted';
  const date = (iso) => iso ? new Date(iso).toLocaleDateString() : '—';

  // "Share every term": a count of each term that can have one and has none.
  // The server works the list out again; this only shows it first.
  let reviewing = $state(false), adding = $state(false), problem = $state(null), added = $state(null);
  const allTerms = (d) => d.terms.flatMap((schema) => schema.terms);
  const proposed = (d) => d.sharing.everyTerm.map((cube) => allTerms(d).find((t) => t.id === cube.dimensions[0]));
  const left = (d) => {
    const reasons = new Map();
    for (const t of allTerms(d)) {
      const why = t.notCountable ?? (t.cubes.length ? 'already counted' : null);
      if (why) reasons.set(why, [...(reasons.get(why) ?? []), t]);
    }
    return [...reasons].map(([why, terms]) => ({ why, terms }));
  };
  // Deleting the dataset: first what would go (or what stands in the way),
  // then the steward types its name to confirm.
  let removal = $state(null), typed = $state(''), deleting = $state(false), deleteProblem = $state(null);
  async function askDelete(d) {
    deleteProblem = null; typed = '';
    try { removal = await datasetRemoval(d.id); } catch (e) { deleteProblem = e.message; }
  }
  async function confirmDelete(d) {
    deleting = true; deleteProblem = null;
    try { await deleteDataset(d.id); go('overview'); }
    catch (e) { deleteProblem = e.message; } finally { deleting = false; }
  }
  const several = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

  async function shareAll(d) {
    adding = true; problem = null;
    try {
      added = (await shareEveryTerm(d.id)).added.length;
      reviewing = false;
      datasets = getDatasets();
    } catch (e) { problem = e.message; } finally { adding = false; }
  }
</script>

<HelpDrawer {...HELP.dataset} />

<Async promise={datasets}>
  {#snippet children(all)}
    {@const d = find(all)}
    {#if !d}
      <h1 class="wf2-title wf2-title-level-1">Dataset not found</h1>
      <p class="wf2-text">There is no dataset called <code class="mono">{nav.params.id}</code> here.</p>
      <Button variant="secondary" to="overview">Back to Overview</Button>
    {:else}
      <p class="wf2-caption"><a href="#/overview">Overview</a> /</p>
      <h1 class="wf2-title wf2-title-level-1">{d.label}</h1>
      <p class="wf2-subtitle">{amount(d)} · {periodRange(d.periods)} ·
        {d.sharing.partners.length ? `shared with ${d.sharing.partners.map((p) => p.label).join(', ')}` : 'not shared yet'}</p>

      <div class="wf2-tabs" role="tablist" style="margin: var(--space-4) 0">
        {#each TABS as t}
          <button type="button" role="tab" class="wf2-tab" class:active={tab === t.id}
            aria-selected={tab === t.id} onclick={() => open(t.id)}>{t.label}</button>
        {/each}
      </div>

      {#if tab === 'about'}
        <Section title="Where it comes from">
          <Panel>
            {#if d.sources.length}
              <div class="wf2-tablewrap"><table class="wf2-table">
                <thead><tr><th>Export</th><th>Adds</th><th>Periods</th><th>Latest</th><th></th></tr></thead>
                <tbody>
                  {#each d.sources as s}
                    <tr>
                      <td>{s.label}</td>
                      <td>{s.records === null ? '—' : `${s.records} ${d.unit ?? 'records'}`}</td>
                      <td>{periodRange(s.periods)}</td>
                      <td>{exportState(s)}</td>
                      <td>{#if mayVisit('source/ingest')}<Button variant="ghost" to={`source/ingest?id=${encodeURIComponent(s.id)}`}>Ingest</Button>{/if}</td>
                    </tr>
                  {/each}
                </tbody>
              </table></div>
              {#if d.sources.length > 1}
                <p class="wf2-caption">Each export fills part of {d.label}.</p>
              {/if}
            {:else}
              <p class="wf2-text">No export fills this dataset yet.</p>
            {/if}
            {#if d.countedAt}<p class="wf2-caption">Counted {new Date(d.countedAt).toLocaleString()}.</p>{/if}
          </Panel>
        </Section>
      {:else if tab === 'terms'}
        {#each d.terms as schema}
          <Section title={schema.label ?? 'Not in a schema'}>
            <Panel>
              {#if schema.namespace}<p class="wf2-caption"><span class="mono">{schema.id}</span> · <span class="mono">{schema.namespace}</span>
                {#if mayVisit('schemas')}· <a href="#/schemas">Schemas</a>{/if}</p>{/if}
              <div class="wf2-tablewrap"><table class="wf2-table">
                <thead><tr><th>Term</th><th>Values</th><th>Shared</th></tr></thead>
                <tbody>
                  {#each schema.terms as q}
                    <tr>
                      <td>{q.text ?? q.id}<span class="col-stats mono">{q.id}</span></td>
                      <td>{values(q)}</td>
                      <td>{shared(q)}</td>
                    </tr>
                  {/each}
                </tbody>
              </table></div>
            </Panel>
          </Section>
        {:else}
          <p class="wf2-text">The vocabulary describes no terms for this dataset.</p>
        {/each}
      {:else if tab === 'sharing'}
        <Section title="The rules">
          <Panel>
            <ul class="wf2-text">
              <li>Only counts leave the Pod, never anyone's answers.</li>
              {#if d.sharing.minPerColumn !== null}
                <li>A term is shared for a period only when at least {d.sharing.minPerColumn} {d.unit ?? 'records'} have a value for it.</li>
                <li>Where two terms are combined, any count under {d.sharing.suppressBelow} is hidden.</li>
              {:else}
                <li>Any count under {d.sharing.suppressBelow} is hidden.</li>
              {/if}
              {#if d.terms.some((s) => s.terms.some((q) => q.neverShared))}
                <li>Never shared: {d.terms.flatMap((s) => s.terms).filter((q) => q.neverShared).length} terms, such as free text.</li>
              {/if}
            </ul>
            {#if d.declared && d.sharing.cubes.length && (can('publish') || can('author-cubes'))}
              <RulesEditor dataset={d} name={(id) => { const cube = d.sharing.cubes.find((c) => c.id === id); return cube ? counts(d, cube) : id; }}
                onsaved={() => (datasets = getDatasets())} />
            {/if}
          </Panel>
        </Section>
        <Section title="Who gets what">
          <Panel>
            {#if d.sharing.partners.length}
              <div class="wf2-tablewrap"><table class="wf2-table">
                <thead><tr><th>Partner</th><th>What they receive</th><th>Since</th></tr></thead>
                <tbody>
                  {#each d.sharing.partners as p}
                    <tr>
                      <td>{p.label}</td>
                      <td>{p.cubes.length} of {d.sharing.cubes.length} counts</td>
                      <td>{date(p.publishedAt)}</td>
                    </tr>
                  {/each}
                </tbody>
              </table></div>
            {:else}
              <p class="wf2-text">Nothing from {d.label} is shared with a partner yet.</p>
            {/if}
            {#if mayVisit('partners')}<Button variant="secondary" to="partners">Partners</Button>{/if}
          </Panel>
        </Section>
        <Section title="What we count">
          <Panel>
            {#if !d.sharing.cubes.length}
              <p class="wf2-text">Nothing from {d.label} is counted yet, so there is nothing to share.</p>
            {:else}
            <div class="wf2-tablewrap"><table class="wf2-table">
              <thead><tr><th>Counts of</th><th>Intended for</th><th>Shared with</th></tr></thead>
              <tbody>
                {#each d.sharing.cubes as cube}
                  <tr>
                    <td>{counts(d, cube)}{#if cube.reducer === 'sum'} (summed){/if}</td>
                    <td>{sharingWords(cube.sharing)}</td>
                    <td>{d.sharing.partners.filter((p) => p.cubes.includes(cube.id)).map((p) => p.label).join(', ') || '—'}</td>
                  </tr>
                {/each}
              </tbody>
            </table></div>
            {/if}
            {#if added}<p class="wf2-text">Added {added} {added === 1 ? 'count' : 'counts'}.</p>{/if}
            {#if mayVisit('catalog/new')}
              <div class="wf2-row" style="gap:12px"><Button variant="ghost" to={`catalog/new?dataset=${encodeURIComponent(d.id)}`}>New cube</Button></div>
            {/if}
            {#if d.sharing.everyTerm.length && (can('map') || can('author-cubes'))}
              {#if !reviewing}
                <p class="wf2-text">{d.sharing.everyTerm.length} {d.sharing.everyTerm.length === 1 ? 'term has' : 'terms have'} no count yet.</p>
                <Button variant="secondary" onclick={() => { reviewing = true; added = null; }}>Share every term</Button>
              {:else}
                <p class="wf2-text">Each of these gets a count of its own, per period. Counts stay in the Pod until a
                  partner is granted {d.label}; then they receive them, under the rules above.</p>
                <p class="wf2-text"><strong>Counted:</strong> {proposed(d).map((t) => t.text ?? t.id).join(', ')}.</p>
                {#each left(d) as group}
                  <p class="wf2-caption">{group.why[0].toUpperCase()}{group.why.slice(1)}: {group.terms.map((t) => t.text ?? t.id).join(', ')}.</p>
                {/each}
                {#if problem}<p class="wf2-text" role="alert">{problem}</p>{/if}
                <div class="wf2-row" style="gap:12px">
                  <Button variant="primary" onclick={() => shareAll(d)} disabled={adding}>{adding ? 'Counting…' : d.sharing.everyTerm.length === 1 ? 'Count this term' : `Count these ${d.sharing.everyTerm.length} terms`}</Button>
                  <Button variant="ghost" onclick={() => { reviewing = false; problem = null; }} disabled={adding}>Cancel</Button>
                </div>
              {/if}
            {/if}
          </Panel>
        </Section>
      {:else}
        <p class="wf2-text">How {d.label} is stored and counted. Changing any of this changes what the other tabs show.</p>
        <Section title="Record classes">
          <Panel>
            <div class="wf2-tablewrap"><table class="wf2-table">
              <thead><tr><th>Class</th><th>Container</th><th>Records</th></tr></thead>
              <tbody>
                {#each d.recordClasses as c}
                  <tr><td class="mono">{c.id}</td><td class="mono">{c.container ?? '—'}</td><td>{c.records ?? '—'}</td></tr>
                {/each}
              </tbody>
            </table></div>
            {#if !d.declared}<p class="wf2-caption">Not declared in datasets.json: this class is a dataset of its own until it is grouped.</p>{/if}
          </Panel>
        </Section>
        <Section title="Sources">
          <Panel>
            <div class="wf2-tablewrap"><table class="wf2-table">
              <thead><tr><th>Source</th><th>Columns</th><th></th></tr></thead>
              <tbody>
                {#each d.sources as s}
                  <tr>
                    <td>{s.label}<span class="col-stats mono">{s.id}</span></td>
                    <td>{s.columns.length}</td>
                    <td><div class="wf2-row" style="gap:6px; flex-wrap:wrap">
                      {#if mayVisit('source/mapping')}<Button variant="ghost" to={`source/mapping?id=${encodeURIComponent(s.id)}`}>Mapping</Button>{/if}
                      {#if mayVisit('records')}<Button variant="ghost" to={`records?source=${encodeURIComponent(s.id)}`}>Records</Button>{/if}
                    </div></td>
                  </tr>
                {/each}
              </tbody>
            </table></div>
            {#if mayVisit('dataset/new')}
              <Button variant="secondary" to={`dataset/new?to=${encodeURIComponent(d.id)}`}>Add a source</Button>
            {/if}
          </Panel>
        </Section>
        {#if can('map')}
          <Section title="Delete this dataset">
            <Panel>
              {#if !removal}
                <p class="wf2-text">Removes {d.label} and everything that is part of it: its counts, its records,
                  the sources that fill it, the terms made from them and its saved comparisons.</p>
                {#if deleteProblem}<p class="wf2-text" role="alert">{deleteProblem}</p>{/if}
                <Button variant="secondary" onclick={() => askDelete(d)}>Delete {d.label}…</Button>
              {:else if removal.problems.length}
                <p class="wf2-text">{d.label} can't be deleted yet:</p>
                <ul class="wf2-text">{#each removal.problems as problem}<li>{problem}</li>{/each}</ul>
                <Button variant="ghost" onclick={() => { removal = null; }}>Close</Button>
              {:else}
                <p class="wf2-text">This removes, and can't be undone:</p>
                <ul class="wf2-text">
                  <li>{several(removal.cubes.length, 'count')}</li>
                  <li>the records in {removal.records.map((r) => r.container).join(', ') || '—'}</li>
                  {#if removal.sources.length}<li>{several(removal.sources.length, 'source')}: {removal.sources.map((s) => s.label).join(', ')}, with the stored file</li>{/if}
                  {#if removal.terms.length}<li>{several(removal.terms.length, 'term')} made from {removal.sources.length === 1 ? 'it' : 'them'}{#if removal.valueSchemes.length}, and {several(removal.valueSchemes.length, 'value list')}{/if}</li>{/if}
                  {#if removal.queries.length}<li>{several(removal.queries.length, 'saved comparison')}: {removal.queries.map((q) => q.label).join(', ')}</li>{/if}
                </ul>
                <label class="wf2-text" for="confirm-delete">Type <strong>{d.label}</strong> to confirm</label>
                <input id="confirm-delete" class="wf2-input" bind:value={typed} autocomplete="off" />
                {#if deleteProblem}<p class="wf2-text" role="alert">{deleteProblem}</p>{/if}
                <div class="wf2-row" style="gap:12px; margin-top: var(--space-3)">
                  <Button variant="danger" onclick={() => confirmDelete(d)} disabled={deleting || typed.trim() !== d.label}>
                    {deleting ? 'Deleting…' : `Delete ${d.label}`}</Button>
                  <Button variant="ghost" onclick={() => { removal = null; }} disabled={deleting}>Cancel</Button>
                </div>
              {/if}
            </Panel>
          </Section>
        {/if}
      {/if}
    {/if}
  {/snippet}
</Async>
